import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Timer, Bomb, Zap, Cable, Lock, Shield, Calculator, Brain, Trophy } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GameMode, GameStats, Language } from '../types';
import useSound from 'use-sound';
import tickSound from '../assets/sounds/tick.wav';
import successSound from '../assets/sounds/success.wav';
import errorSound from '../assets/sounds/error.wav';
import defuseSound from '../assets/sounds/defuse.wav';
import explosionSound from '../assets/sounds/explosion.wav';

interface MathBombGameProps {
  mode: GameMode;
  onReset: () => void;
  language: Language;
}

interface Module {
  id: number;
  type: 'multiplication' | 'wires' | 'code' | 'number';
  solved: boolean;
  data: any;
}

const MathBombGame: React.FC<MathBombGameProps> = ({ mode, onReset, language }) => {
  const [level, setLevel] = useState(1);
  const [remainingTime, setRemainingTime] = useState(90);
  const [modules, setModules] = useState<Module[]>([]);
  const [selectedModule, setSelectedModule] = useState<number | null>(null);
  const [gameOver, setGameOver] = useState(false);
  const [success, setSuccess] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showExplosion, setShowExplosion] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [stats, setStats] = useState<GameStats>({
    questionsAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    totalTime: 0,
    averageResponseTime: 0,
  });

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const [playTick, { stop: stopTick }] = useSound(tickSound, {
    volume: 0.4,
    loop: true,
  });
  const [playSuccess] = useSound(successSound, { volume: 0.5 });
  const [playError] = useSound(errorSound, { volume: 0.3 });
  const [playDefuse] = useSound(defuseSound, { volume: 0.5 });
  const [playExplosion] = useSound(explosionSound, { volume: 0.4 });

  const playSound = (sound: () => void) => {
    if (soundEnabled) {
      try {
        sound();
      } catch (error) {
        console.warn('Error playing sound:', error);
      }
    }
  };

  useEffect(() => {
    if (remainingTime <= 0 && !gameOver && !success) {
      setGameOver(true);
      setShowExplosion(true);
      playSound(playExplosion);
      setTimeout(() => {
        setShowExplosion(false);
      }, 2000);
    }
  }, [remainingTime, gameOver, success]);

  useEffect(() => {
    return () => {
      stopTick();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    initializeGame();
  }, [level]);

  useEffect(() => {
    if (!gameOver && !success && soundEnabled && remainingTime > 0) {
      stopTick();
      playTick();
    }
  }, [gameOver, success, soundEnabled, remainingTime]);

  useEffect(() => {
    if (gameOver || success || !soundEnabled) {
      stopTick();
    }
  }, [gameOver, success, soundEnabled]);

  useEffect(() => {
    if (remainingTime > 0 && !gameOver && !success) {
      timerRef.current = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            setGameOver(true);
            setShowExplosion(true);
            playSound(playExplosion);
            setTimeout(() => {
              setShowExplosion(false);
            }, 2000);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
  }, [remainingTime, gameOver, success, soundEnabled]);

  const initializeGame = () => {
    const initialTime = level === 1 ? 90 : level === 2 ? 60 : 30;
    setRemainingTime(initialTime);
    setGameOver(false);
    setSuccess(false);
    setSelectedModule(null);
    generateModules();
  };

  const generateModules = () => {
    const moduleTypes: Module['type'][] = ['multiplication', 'wires', 'code', 'number'];
    const newModules: Module[] = moduleTypes.map((type, index) => ({
      id: index,
      type,
      solved: false,
      data: generateModuleData(type)
    }));
    setModules(newModules);
  };

  const generateModuleData = (type: Module['type']) => {
    switch (type) {
      case 'multiplication': {
        const numbers = Array.from({ length: 3 }, () => ({
          a: Math.floor(Math.random() * 10) + 1,
          b: Math.floor(Math.random() * 10) + 1,
        }));
        const targetIndex = Math.floor(Math.random() * 3);
        const target = numbers[targetIndex].a * numbers[targetIndex].b;
        return { numbers, target };
      }
      case 'wires': {
        const colors = ['red', 'green', 'blue'];
        const equations = colors.map(() => {
          const a = Math.floor(Math.random() * 10) + 1;
          const b = Math.floor(Math.random() * 10) + 1;
          return { a, b, result: a * b };
        });
        
        const correctIndex = Math.floor(Math.random() * 3);
        const targetResult = equations[correctIndex].result;
        
        return { 
          colors, 
          equations,
          correctIndex,
          targetResult
        };
      }
      case 'code': {
        return Array.from({ length: 3 }, () => ({
          a: Math.floor(Math.random() * 10) + 1,
          b: Math.floor(Math.random() * 10) + 1,
        }));
      }
      case 'number': {
        const isPrimeQuestion = Math.random() < 0.5;
        const numbers = [];
      
        if (isPrimeQuestion) {
          const primes = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37];
          const fakeNumbers = Array.from({ length: 2 }, () => {
            let n;
            do {
              n = Math.floor(Math.random() * (10 + level * 10)) + 2;
            } while (primes.includes(n));
            return n;
          });
          numbers.push(primes[Math.floor(Math.random() * Math.min(primes.length, 5 + level * 2))]);
          numbers.push(...fakeNumbers);
        } else {
          const roots = [2, 3, 4, 5, 6, 7, 8, 9];
          const correct = roots[Math.floor(Math.random() * Math.min(roots.length, 3 + level * 2))];
          const fakeNumbers = Array.from({ length: 2 }, () => {
            let n;
            do {
              n = Math.floor(Math.random() * (20 + level * 5)) + 1;
            } while (Number.isInteger(Math.sqrt(n)));
            return n;
          });
          numbers.push(correct * correct);
          numbers.push(...fakeNumbers);
        }
      
        return {
          numbers: numbers.sort(() => Math.random() - 0.5),
          questionType: isPrimeQuestion ? 'prime' : 'square'
        };
      }
    }
  };

  const handleModuleClick = (moduleId: number) => {
    if (!modules[moduleId].solved) {
      setSelectedModule(moduleId);
    }
  };

  const handleAnswer = async (moduleId: number, answer: any) => {
    const module = modules[moduleId];
    let correct = false;

    switch (module.type) {
      case 'multiplication':
        correct = checkMultiplicationAnswer(module.data, answer);
        break;
      case 'wires':
        correct = checkWiresAnswer(module.data, answer);
        break;
      case 'code':
        correct = checkCodeAnswer(module.data, answer);
        break;
      case 'number':
        correct = checkNumberAnswer(module.data, answer);
        break;
    }

    if (correct) {
      playSound(playSuccess);
      const updatedModules = modules.map((m, i) =>
        i === moduleId ? { ...m, solved: true } : m
      );
      setModules(updatedModules);
      setSelectedModule(null);

      if (updatedModules.every(m => m.solved)) {
        setSuccess(true);
        playSound(playDefuse);
        if (level < 3) {
          setTimeout(() => {
            setLevel(level + 1);
          }, 2000);
        } else {
          setShowVictory(true);
          saveProgress();
        }
      }
    } else {
      playSound(playError);
      const newTime = Math.max(0, remainingTime - 10);
      setRemainingTime(newTime);
      
      if (newTime <= 0) {
        setGameOver(true);
        setShowExplosion(true);
        playSound(playExplosion);
        setTimeout(() => {
          setShowExplosion(false);
        }, 2000);
      }
    }

    const newStats = {
      ...stats,
      questionsAnswered: stats.questionsAnswered + 1,
      correctAnswers: stats.correctAnswers + (correct ? 1 : 0),
      incorrectAnswers: stats.incorrectAnswers + (correct ? 0 : 1),
    };
    setStats(newStats);
  };

  const checkMultiplicationAnswer = (data: any, answer: number) => {
    return data.numbers[answer].a * data.numbers[answer].b === data.target;
  };

  const checkWiresAnswer = (data: any, answer: number) => {
    return answer === data.correctIndex;
  };

  const checkCodeAnswer = (data: any, answer: number[]) => {
    return answer.every((num, i) => num === data[i].a * data[i].b);
  };

  const checkNumberAnswer = (data: any, answer: number) => {
    const numbers = data.numbers;
    const num = numbers[answer];
    const isSquare = Math.sqrt(num) % 1 === 0;
    const isPrime = (n: number) => {
      if (n <= 1) return false;
      if (n <= 3) return true;
      if (n % 2 === 0 || n % 3 === 0) return false;
      for (let i = 5; i * i <= n; i += 6) {
        if (n % i === 0 || n % (i + 2) === 0) return false;
      }
      return true;
    };
    return data.questionType === 'prime' ? isPrime(num) : isSquare;
  };

  const saveProgress = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('user_progress').insert({
        user_id: user.id,
        operation: 'bomb',
        mode: 'defuse',
        questions_answered: stats.questionsAnswered,
        correct_answers: stats.correctAnswers,
        average_response_time: (90 - remainingTime) / stats.correctAnswers,
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const renderModule = (module: Module) => {
    switch (module.type) {
      case 'multiplication':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-center">
              {language === 'en' ? 'Find the pair that equals' : 'Trouve la paire égale à'} {module.data.target}
            </h3>
            <div className="grid grid-cols-1 gap-4">
              {module.data.numbers.map((pair: any, index: number) => (
                <button
                  key={index}
                  onClick={() => handleAnswer(module.id, index)}
                  className="p-4 bg-gray-900 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  {pair.a} × {pair.b}
                </button>
              ))}
            </div>
          </div>
        );

      case 'wires':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-center">
              {language === 'en' 
                ? `Cut the wire with the equation that equals ${module.data.targetResult}`
                : `Coupe le fil dont l'équation égale ${module.data.targetResult}`}
            </h3>
            <div className="grid grid-cols-1 gap-4">
              {module.data.colors.map((color: string, index: number) => {
                const equation = module.data.equations[index];
                return (
                  <button
                    key={index}
                    onClick={() => handleAnswer(module.id, index)}
                    className={`p-4 rounded-lg transition-colors flex items-center justify-between ${
                      color === 'red' ? 'bg-red-900 hover:bg-red-800' :
                      color === 'green' ? 'bg-green-900 hover:bg-green-800' :
                      'bg-blue-900 hover:bg-blue-800'
                    }`}
                  >
                    <span>{equation.a} × {equation.b} = ?</span>
                    <span>{color.toUpperCase()} WIRE</span>
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 'code':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-center">
              {language === 'en' ? 'Enter the multiplication results' : 'Entre les résultats des multiplications'}
            </h3>
            <div className="grid grid-cols-1 gap-4">
              {Array.isArray(module.data) && module.data.map((pair: any, index: number) => (
                <div key={index} className="flex items-center gap-2">
                  <span>{pair.a} × {pair.b} = </span>
                  <input
                    type="number"
                    className="bg-gray-900 p-2 rounded w-20 text-center"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const inputs = Array.from(document.querySelectorAll('input[type="number"]'))
                          .map(input => parseInt((input as HTMLInputElement).value));
                        if (inputs.every(num => !isNaN(num))) {
                          handleAnswer(module.id, inputs);
                        }
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        );

      case 'number':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-center">
              {module.data.questionType === 'prime' ? (
                language === 'en' ? 'Select the prime number' : 'Sélectionne le nombre premier'
              ) : (
                language === 'en' ? 'Select the perfect square' : 'Sélectionne le carré parfait'
              )}
            </h3>
            <div className="grid grid-cols-1 gap-4">
              {module.data.numbers.map((num: number, index: number) => (
                <button
                  key={index}
                  onClick={() => handleAnswer(module.id, index)}
                  className="p-4 bg-gray-900 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0" style={{
      background: `
        linear-gradient(45deg, transparent 48%, rgba(218, 165, 32, 0.1) 50%, transparent 52%),
        linear-gradient(-45deg, transparent 48%, rgba(218, 165, 32, 0.1) 50%, transparent 52%),
        radial-gradient(circle at 50% 50%, rgba(218, 165, 32, 0.05) 0%, transparent 20%),
        linear-gradient(90deg, rgba(218, 165, 32, 0.03) 1px, transparent 1px),
        linear-gradient(0deg, rgba(218, 165, 32, 0.03) 1px, transparent 1px),
        linear-gradient(45deg, rgba(218, 165, 32, 0.05) 1px, transparent 2px),
        linear-gradient(-45deg, rgba(218, 165, 32, 0.05) 1px, transparent 2px),
        linear-gradient(90deg, rgba(218, 165, 32, 0.04) 0px, transparent 50px),
        linear-gradient(0deg, rgba(218, 165, 32, 0.04) 0px, transparent 50px),
        radial-gradient(circle at 25% 25%, rgba(218, 165, 32, 0.1) 0%, transparent 10%),
        #002800
      `,
      backgroundSize: '100px 100px, 100px 100px, 200px 200px, 10px 10px, 10px 10px, 20px 20px, 20px 20px, 50px 50px, 50px 50px, 100px 100px, 100%',
      backgroundPosition: '0 0, 0 0, 0 0, 5px 5px, 5px 5px, 0 0, 0 0, 25px 25px, 25px 25px, 0 0, 0 0'
    }}>
      {showExplosion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="relative">
            <div className="absolute inset-0 bg-red-500 animate-ping rounded-full opacity-30"></div>
            <div className="absolute inset-0 bg-orange-500 animate-ping rounded-full opacity-20 delay-100"></div>
            <div className="absolute inset-0 bg-yellow-500 animate-ping rounded-full opacity-10 delay-200"></div>
            <div className="text-6xl transform scale-150 animate-bounce">💥</div>
          </div>
        </div>
      )}

      {showVictory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="bg-slate-900 p-8 rounded-xl border-2 border-green-500 text-center max-w-md mx-4 animate-[fadeZoomIn_0.5s_ease-out_forwards]">
            <Trophy className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-green-500 mb-4">
              {language === 'en' ? 'Mission Accomplished!' : 'Mission Accomplie !'}
            </h2>
            <p className="text-slate-300 mb-6">
              {language === 'en'
                ? 'Congratulations! You\'ve successfully completed all levels and proven yourself to be an expert bomb defuser!'
                : 'Félicitations ! Vous avez réussi tous les niveaux et prouvé que vous êtes un expert en désamorçage de bombes !'}
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => {
                  setShowVictory(false);
                  setLevel(1);
                  initializeGame();
                }}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                {language === 'en' ? 'Play Again' : 'Rejouer'}
              </button>
              <button
                onClick={onReset}
                className="px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
              >
                {language === 'en' ? 'Back to Menu' : 'Retour au Menu'}
              </button>
            </div>
          </div>
        </div>
      )}

      {gameOver && !showExplosion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="bg-slate-900 p-8 rounded-xl border-2 border-red-500 text-center max-w-md mx-4">
            <div className="text-6xl mb-6">💥</div>
            <h2 className="text-2xl font-bold text-red-500 mb-4">
              {language === 'en' ? 'BOMB EXPLODED!' : 'BOMBE EXPLOSÉE !'}
            </h2>
            <p className="text-slate-300 mb-6">
              {language === 'en'
                ? 'The bomb detonated. Mission failed.'
                : 'La bombe a explosé. Mission échouée.'}
            </p>
            <button
              onClick={() => {
                setGameOver(false);
                setLevel(1);
                initializeGame();
              }}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              {language === 'en' ? 'Try Again' : 'Réessayer'}
            </button>
          </div>
        </div>
      )}

      <div className="absolute inset-0 bg-black/20 backdrop-blur-[1px]">
        <div className="p-6 flex items-center justify-between border-b border-green-900/30" 
             style={{ background: 'linear-gradient(180deg, rgba(0,24,0,0.9) 0%, rgba(0,12,0,0.8) 100%)' }}>
          <button
            onClick={() => {
              stopTick();
              onReset();
            }}
            className="flex items-center text-green-500 hover:text-green-400 transition-colors font-mono"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            {language === 'en' ? '[ EXIT_SYSTEM ]' : '[ QUITTER_SYSTÈME ]'}
          </button>
          
          <div className="flex items-center gap-6">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`p-2 rounded transition-colors font-mono ${
                soundEnabled ? 'bg-green-900/30 text-green-500' : 'bg-red-900/30 text-red-500'
              }`}
            >
              {soundEnabled ? '[ AUDIO_ON ]' : '[ AUDIO_OFF ]'}
            </button>
            
            <div className={`font-mono text-2xl font-bold ${
              remainingTime <= 10 ? 'text-red-500 animate-pulse' : 'text-green-500'
            }`} style={{ fontFamily: 'Digital-7, monospace' }}>
              {Math.floor(remainingTime / 60).toString().padStart(2, '0')}:
              {(remainingTime % 60).toString().padStart(2, '0')}
            </div>
            
            <div className="text-lg font-mono text-green-500">
              {language === 'en' ? `[ LEVEL_${level} ]` : `[ NIVEAU_${level} ]`}
            </div>
          </div>
        </div>

        <div className="flex-1 p-6">
          <div className="max-w-6xl mx-auto h-full rounded-lg p-8 border border-green-900/30 shadow-[0_0_15px_rgba(0,255,0,0.1)] backdrop-blur-sm"
               style={{ background: 'linear-gradient(135deg, rgba(0,24,0,0.95) 0%, rgba(0,12,0,0.9) 100%)' }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full relative">
              <div className="space-y-4">
                {modules.map((module) => (
                  <button
                    key={module.id}
                    onClick={() => handleModuleClick(module.id)}
                    disabled={module.solved}
                    className={`w-full p-4 rounded-lg flex items-center gap-3 transition-all transform hover:scale-105
                      ${module.solved
                        ? 'bg-green-900/20 text-green-500 border border-green-500/50'
                        : 'bg-black/50 hover:bg-black/70 text-green-500 border border-green-900/50 hover:border-green-500/50'
                      } font-mono relative overflow-hidden`}
                    style={{
                      boxShadow: module.solved 
                        ? '0 0 10px rgba(0,255,0,0.2)' 
                        : '0 0 10px rgba(0,255,0,0.1)',
                      background: module.solved 
                        ? 'linear-gradient(45deg, rgba(0,255,0,0.1) 0%, rgba(0,24,0,0.2) 100%)'
                        : 'linear-gradient(45deg, rgba(0,24,0,0.3) 0%, rgba(0,12,0,0.4) 100%)'
                    }}
                  >
                    <div className="bg-black/50 p-2 rounded">
                      {module.type === 'multiplication' && <Calculator className="h-5 w-5" />}
                      {module.type === 'wires' && <Cable className="h-5 w-5" />}
                      {module.type === 'code' && <Lock className="h-5 w-5" />}
                      {module.type === 'number' && <Brain className="h-5 w-5" />}
                    </div>

                    <span className="flex-1">
                      {language === 'en'
                        ? module.type === 'multiplication' ? 'CALC_MODULE'
                          : module.type === 'wires' ? 'WIRE_MODULE'
                          : module.type === 'code' ? 'CODE_MODULE'
                          : 'NUM_MODULE'
                        : module.type === 'multiplication' ? 'MODULE_CALC'
                          : module.type === 'wires' ? 'MODULE_CABLE'
                          : module.type === 'code' ? 'MODULE_CODE'
                          : 'MODULE_NUM'
                      }
                    </span>

                    {module.solved ? (
                      <div className="flex items-center gap-2 text-green-500">
                        <span className="text-xs">[ DEFUSED ]</span>
                        <Zap className="h-5 w-5" />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-red-500 animate-pulse">
                        <span className="text-xs">[ ARMED ]</span>
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      </div>
                    )}

                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-500/5 to-transparent -skew-x-12 transform"></div>
                  </button>
                ))}
              </div>

              <div className="rounded-lg border border-green-900/30 p-6 relative overflow-hidden"
                   style={{ background: 'linear-gradient(135deg, rgba(0,24,0,0.95) 0%, rgba(0,12,0,0.9) 100%)' }}>
                {selectedModule !== null ? (
                  <div className="text-green-500 font-mono">
                    {renderModule(modules[selectedModule])}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full">
                    <div className="relative">
                      <Bomb className="h-32 w-32 text-red-500 animate-pulse" />
                      <div className="absolute -inset-4 bg-red-500/20 animate-ping rounded-full"></div>
                    </div>
                    <p className="mt-6 text-center text-green-500 font-mono">
                      {language === 'en'
                        ? '[ SELECT_MODULE_TO_DEFUSE ]'
                        : '[ SELECTIONNER_MODULE ]'}
                    </p>
                  </div>
                )}

                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-green-500/20 rounded-tl-lg"></div>
                  <div className="absolute top-0 right-0 w-16 h-16 border-r-2 border-t-2 border-green-500/20 rounded-tr-lg"></div>
                  <div className="absolute bottom-0 left-0 w-16 h-16 border-l-2 border-b-2 border-green-500/20 rounded-bl-lg"></div>
                  <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-green-500/20 rounded-br-lg"></div>
                </div>
              </div>
            </div>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-4">
              {modules.map((module) => (
                <div
                  key={module.id}
                  className={`w-3 h-3 rounded-full ${
                    module.solved 
                      ? 'bg-green-500 shadow-lg shadow-green-500/50' 
                      : 'bg-red-500 shadow-lg shadow-red-500/50 animate-pulse'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MathBombGame;